import React, { useState, useEffect, useContext } from 'react';
import { Link, Navigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Modal from '../components/Modal';
import { AuthContext } from '../context/AuthContext';
import { getCustomers, createCustomer } from '../utils/api';
import { useToast } from '../context/ToastContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faChevronRight, faPlus, faUsers, faMicrochip } from '@fortawesome/free-solid-svg-icons';
import { ROLE_SUPERADMIN } from '../utils/roles';

const OrgPage = () => {
        const { user } = useContext(AuthContext);
        const [customers, setCustomers] = useState([]);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState(null);
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [newCustomerId, setNewCustomerId] = useState('');
        const [newCustomerName, setNewCustomerName] = useState('');
        const { showToast } = useToast();

        useEffect(() => {
                const fetchCustomers = async () => {
                        try {
                                const response = await getCustomers();
                                if (!response.ok) {
                                        throw new Error('Failed to fetch customers');
                                }
                                const data = await response.json();
                                setCustomers(data.data || []);
                        } catch (err) {
                                setError(err.message);
                        } finally {
                                setLoading(false);
                        }
                };

                fetchCustomers();
        }, []);

        const handleCreateCustomer = async () => {
                try {
                        const response = await createCustomer({
                                id: parseInt(newCustomerId),
                                name: newCustomerName
                        });
                        if (!response.ok) {
                                throw new Error('Failed to create customer');
                        }
                        const data = await response.json();
                        setCustomers(prev => [...prev, data.data]);
                        setIsModalOpen(false);
                        setNewCustomerId('');
                        setNewCustomerName('');
                        showToast('success', 'Customer created successfully');
                } catch (err) {
                        showToast('error', err.message);
                }
        };

        if (user?.roleId !== ROLE_SUPERADMIN) {
                return <Navigate to="/" replace />;
        }

        if (loading) {
                return <div className="page-gradient min-h-screen flex items-center justify-center">Loading...</div>;
        }

        if (error) {
                return <div className="page-gradient min-h-screen flex items-center justify-center text-status-critical">{error}</div>;
        }

        return (
                <div className="page-gradient min-h-screen">
                        <Header />

                        <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
                                <section className="mb-8 flex items-center justify-between">
                                        <div>
                                                <h1 className="text-3xl font-bold text-text-strong mb-2">Organizations</h1>
                                                <p className="text-text-weak">Manage all customers</p>
                                        </div>
                                        <button
                                                onClick={() => setIsModalOpen(true)}
                                                data-testid="org-new-open"
                                                className="inline-flex items-center px-4 py-2 bg-brand text-white rounded-xl hover:bg-brand/90 transition-colors"
                                        >
                                                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                                New Customer
                                        </button>
                                </section>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {customers.map(customer => (
                                                <div
                                                        key={customer.id}
                                                        data-testid="org-card"
                                                        data-customer-id={customer.id}
                                                        className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-card p-6 hover:shadow-lg transition-all duration-200"
                                                >
                                                        <div className="flex items-center justify-between mb-4">
                                                                <div className="flex items-center space-x-4">
                                                                        <div className="w-12 h-12 bg-brand/10 rounded-xl flex items-center justify-center">
                                                                                <FontAwesomeIcon icon={faBuilding} className="text-brand text-xl" />
                                                                        </div>
                                                                        <div>
                                                                                <h3 data-testid="org-name" className="font-semibold text-text-strong">{customer.name}</h3>
                                                                                <p className="text-sm text-text-weak">CVR: {customer.id}</p>
                                                                        </div>
                                                                </div>
                                                        </div>
                                                        <div className="grid grid-cols-3 gap-2">
                                                                <Link
                                                                        to={`/org/${customer.id}`}
                                                                        data-testid="org-link-dashboard"
                                                                        className="inline-flex items-center justify-center px-2 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                                                >
                                                                        <FontAwesomeIcon icon={faChevronRight} className="mr-1" />
                                                                        Dashboard
                                                                </Link>
                                                                <Link
                                                                        to={`/org/${customer.id}/users`}
                                                                        data-testid="org-link-users"
                                                                        className="inline-flex items-center justify-center px-2 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                                                >
                                                                        <FontAwesomeIcon icon={faUsers} className="mr-1" />
                                                                        Users
                                                                </Link>
                                                                {user?.roleId === ROLE_SUPERADMIN && (
                                                                        <Link
                                                                                to={`/org/${customer.id}/devices`}
                                                                                data-testid="org-link-devices"
                                                                                className="inline-flex items-center justify-center px-2 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                                                        >
                                                                                <FontAwesomeIcon icon={faMicrochip} className="mr-1" />
                                                                                Devices
                                                                        </Link>
                                                                )}
                                                        </div>
                                                </div>
                                        ))}
                                </div>

                                {customers.length === 0 && (
                                        <div className="text-center py-12">
                                                <p className="text-lg text-gray-600">No customers found.</p>
                                        </div>
                                )}
                        </main>

                        <Footer />

                        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
                                <form onSubmit={(e) => { e.preventDefault(); handleCreateCustomer(); }}>
                                        <h3 className="text-lg font-bold mb-4">Create New Customer</h3>
                                        <div className="space-y-4">
                                                <div>
                                                        <label htmlFor="customer-id" className="block text-sm font-medium text-gray-700">
                                                                CVR Number
                                                        </label>
                                                        <input
                                                                type="number"
                                                                id="customer-id"
                                                                data-testid="org-customer-id"
                                                                value={newCustomerId}
                                                                onChange={(e) => setNewCustomerId(e.target.value)}
                                                                className="p-2 border rounded-md w-full"
                                                                placeholder="Enter CVR number"
                                                        />
                                                </div>
                                                <div>
                                                        <label htmlFor="customer-name" className="block text-sm font-medium text-gray-700">
                                                                Customer Name
                                                        </label>
                                                        <input
                                                                type="text"
                                                                id="customer-name"
                                                                data-testid="org-customer-name"
                                                                value={newCustomerName}
                                                                onChange={(e) => setNewCustomerName(e.target.value)}
                                                                className="p-2 border rounded-md w-full"
                                                                placeholder="Enter customer name"
                                                        />
                                                </div>
                                        </div>
                                        <div className="mt-6 flex justify-end space-x-4">
                                                <button
                                                        type="button"
                                                        onClick={() => setIsModalOpen(false)}
                                                        data-testid="org-cancel"
                                                        className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:text-sm"
                                                >
                                                        Cancel
                                                </button>
                                                <button
                                                        type="submit"
                                                        data-testid="org-submit"
                                                        disabled={!newCustomerId.trim() || !newCustomerName.trim()}
                                                        className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand text-base font-medium text-white hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:text-sm disabled:bg-gray-300"
                                                >
                                                        Create
                                                </button>
                                        </div>
                                </form>
                        </Modal>
                </div>
        );
};

export default OrgPage;
